import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Seo from "../components/Seo";
import Breadcrumbs, { type BreadcrumbItem } from "../components/Breadcrumbs";
import Loader from "../components/Loader";
import type { Crumb } from "../utils/structuredData";
import Blueprint, { blueprintSlots } from "../components/catalog/Blueprint";
import QuestionStep from "../components/catalog/QuestionStep";
import Welcome, { BuildingQueue } from "../components/catalog/Welcome";
import QueueHub from "../components/catalog/QueueHub";
import QueueItem from "../components/catalog/QueueItem";
import YourPlan, { NothingFits, ServiceSuggestions } from "../components/catalog/YourPlan";
import { BrowseAll, ServicePage } from "../components/catalog/Browse";
import CatalogCheckout from "../components/catalog/CatalogCheckout";
import DetailsStep from "../components/catalog/DetailsStep";
import { StageBar, prefersReducedMotion } from "../components/catalog/ui";
import {
  type QueueEntry, type QuestionId, acknowledgement, answersForDashboard, detailsNote, fitFor, joinWords, questionList,
  stageOf,
} from "../components/catalog/assessment";
import { type CatalogSession, useCatalogSession } from "../hooks/useCatalogSession";
import {
  type Answers, type CatalogOffering, type CatalogSuggestion, type CartItem, CatalogError, catalogApi, type CatalogSchema, type Listing,
  type Picks, type Setup,
} from "../services/catalogApi";

type Stage = "welcome" | "q" | "building" | "details" | "hub" | "item" | "plan" | "book" | "nofit" | "browse" | "browseItem"
  | "browseService";

const crumbs: Crumb[] = [
  { name: "Home", path: "/" },
  { name: "Catalog", path: "/catalog" },
];
const MAX_QUEUE = 5;
const MAX_DETAILS = 1500;
const UNAVAILABLE = "We can't load the catalog right now. Please try again in a few minutes.";

// The title in the slim bar above each step. The welcome screen has its own hero; the item screen shows its position.
const TITLES: Record<Exclude<Stage, "welcome" | "item">, string> = {
  q: "Your assessment", building: "Your assessment", details: "Your curated list", hub: "Your curated list",
  plan: "Your plan", book: "Book a call to go over your plan", nofit: "Your curated list",
  browse: "Everything in our catalog", browseItem: "Everything in our catalog", browseService: "Everything in our catalog",
};

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

/**
 * The catalog: a short assessment that fills in the visitor's blueprint, then a curated list of the applications and
 * services matched to their problems, which they step through one at a time, then their plan and a call to go over it.
 * Visitors can also browse everything and add to their plan from there. Plain language throughout: no prices, plans or
 * technical details. Answers and the curated list stay in this browser between visits.
 */
const Catalog: React.FC = () => {
  const { session, update, saved, restore, reset } = useCatalogSession();
  const { answers, details, done, setup, queue, decided } = session;
  const [schema, setSchema] = useState<CatalogSchema | null>(null);
  const [stage, setStage] = useState<Stage>("welcome");
  const [question, setQuestion] = useState<QuestionId>("problem");
  const [ack, setAck] = useState("");
  const [editing, setEditing] = useState(false);
  const [buildStep, setBuildStep] = useState(0);
  const [fanned, setFanned] = useState(true);
  const [position, setPosition] = useState(0);
  const [browse, setBrowse] = useState<Listing[] | null>(null);
  // The application opened from Browse everything.
  const [browsing, setBrowsing] = useState<Listing | null>(null);
  // The service opened from Browse everything.
  const [browsingService, setBrowsingService] = useState<CatalogOffering | null>(null);
  const [suggestions, setSuggestions] = useState<CatalogSuggestion[]>([]);
  // The dashboard's follow-up question when it could put something together with one more answer.
  const [followUp, setFollowUp] = useState<{ question: string; asked: number } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Which way the question or application card slides in.
  const [direction, setDirection] = useState<"next" | "back">("next");
  const topRef = useRef<HTMLDivElement>(null);
  // `?add=<service id>` (the Virtual Archives page links here) adds that service to the plan and opens its page.
  const [params, setParams] = useSearchParams();

  const loadSchema = useCallback(() => {
    setNotice(null);
    catalogApi.schema().then(setSchema).catch(() => setNotice(UNAVAILABLE));
  }, []);
  useEffect(loadSchema, [loadSchema]);

  useEffect(() => {
    const top = topRef.current?.getBoundingClientRect().top;
    if (top !== undefined && top < 0) window.scrollTo?.({ top: window.scrollY + top - 80 });
  }, [stage, question, position, browsing, browsingService]);

  const fail = (error: unknown) => setNotice(error instanceof CatalogError ? error.message : UNAVAILABLE);
  const list = useMemo(() => questionList(answers, details), [answers, details]);
  const followUps = list.filter((q) => stageOf(q) === 0 && q !== "problem");

  /** The workspace answer decides where things run; a skipped one is treated as "several different apps". */
  const resolvePicks = useCallback(async (picks: Picks): Promise<Setup | null> => {
    if (!schema) return null;
    const workspace = schema.picks.find((p) => p.id === "workspace");
    const fallback = workspace?.options.find((o) => o.id === "several")?.id ?? workspace?.options[0]?.id;
    return catalogApi.resolve({ ...picks, workspace: picks.workspace ?? fallback });
  }, [schema]);

  const ask = (next: QuestionId, reaction: string) => {
    setDirection(stage === "q" && list.indexOf(next) < list.indexOf(question) ? "back" : "next");
    setQuestion(next);
    setAck(reaction);
    setStage("q");
  };

  const build = useCallback(async (from: CatalogSession, asked = 0) => {
    setStage("building");
    setBuildStep(0);
    const steps = prefersReducedMotion() ? 0 : 650;
    const animation = (async () => { for (let i = 1; i <= 4; i++) { await wait(steps); setBuildStep(i); } })();
    try {
      const resolved = from.setup ?? await resolvePicks(from.answers.picks);
      if (!resolved) throw new Error("No setup");
      const sent = answersForDashboard(from.answers, from.details);
      const [catalog, ranked] = await Promise.all([catalogApi.listings(resolved), catalogApi.match(resolved, sent, [])]);
      setSuggestions(ranked.suggestions);
      let question: string | null = null;
      const byId = new Map(catalog.listings.map((l) => [l.id, l]));
      let entries: QueueEntry[] = ranked.matches.slice(0, MAX_QUEUE).flatMap((m) => {
        const listing = byId.get(m.id);
        return listing ? [{ listing, reasons: m.reasons, fit: fitFor(m, false) }] : [];
      });
      if (!ranked.matched) {
        const composed = await catalogApi.compose(resolved, sent, sent.problems, asked);
        entries = composed.buildable
          ? [{ listing: composed.listing, reasons: [], fit: "Possible", token: composed.token }]
          : (sent.problems.length ? [] : entries);
        if (!composed.buildable && composed.needs_details && composed.question) question = composed.question;
      }
      await animation;
      // Applications added while browsing stay in the list, after the ones matched to the visitor's answers.
      const picked = from.queue.filter((e) => e.picked && from.decided[e.listing.id] === "added" &&
                                             !entries.some((n) => n.listing.id === e.listing.id));
      const listed = [...entries, ...picked];
      const keep = Object.fromEntries(Object.entries(from.decided).filter(
        ([id, d]) => d === "added" && listed.some((e) => e.listing.id === id)));
      update({ setup: resolved, queue: listed, decided: keep, viewed: [], offered: ranked.offerings });
      setEditing(false);
      if (!entries.length && question) { setFollowUp({ question, asked }); setStage("details"); return; }
      if (!entries.length) { setStage("nofit"); return; }
      setFanned(prefersReducedMotion());
      setStage("hub");
      window.setTimeout(() => setFanned(true), 80);
    } catch (error) {
      await animation;
      fail(error);
      setStage("welcome");
    }
  }, [resolvePicks, update]);

  const advance = (from: QuestionId, next: CatalogSession, reaction: string) => {
    const order = questionList(next.answers, next.details);
    const after = order.slice(order.indexOf(from) + 1);
    const following = editing
      ? after.find((q) => !next.done.includes(q)) ?? order.find((q) => !next.done.includes(q))
      : after[0];
    if (following) ask(following, reaction);
    else void build(next);
  };

  const commit = (from: QuestionId, nextAnswers: Answers, nextDetails: typeof details, reaction?: string) => {
    const next: CatalogSession = { ...session, answers: nextAnswers, details: nextDetails,
                                   done: [...new Set([...done, from])] };
    update({ answers: nextAnswers, details: nextDetails, done: next.done });
    advance(from, next, reaction ?? acknowledgement(from, nextAnswers, nextDetails));
  };

  const change = (nextAnswers: Answers, nextDetails: typeof details, move?: boolean) => {
    update({ answers: nextAnswers, details: nextDetails });
    if (["workspace", "data", "payment"].includes(question) && nextAnswers.picks.workspace) {
      resolvePicks(nextAnswers.picks).then((resolved) => resolved && update({ setup: resolved })).catch(fail);
    }
    if (move) window.setTimeout(() => commit(question, nextAnswers, nextDetails), prefersReducedMotion() ? 0 : 260);
  };

  const start = () => {
    setEditing(false);
    ask(list.find((q) => !done.includes(q)) ?? "problem", "");
  };

  const jumpTo = (q: QuestionId) => {
    setEditing(true);
    ask(q, stage === "q" ? "" : "No problem. Change anything you like.");
  };

  const previous = () => {
    const i = list.indexOf(question);
    if (i <= 0) setStage(queue.length ? "hub" : "welcome");
    else ask(list[i - 1], "");
  };

  const openItem = (i: number) => {
    const id = queue[i]?.listing.id;
    if (!id) return;
    update((s) => ({ viewed: [...new Set([...s.viewed, id])] }));
    setDirection(stage === "item" && i < position ? "back" : "next");
    setPosition(i);
    setStage("item");
  };

  const next = () => (position + 1 < queue.length ? openItem(position + 1) : setStage("plan"));

  const openBrowse = async () => {
    setStage("browse");
    await loadBrowse();
  };

  const loadBrowse = async () => {
    if (browse?.length) return;
    try {
      const resolved = setup ?? await resolvePicks({});
      setBrowse(resolved ? (await catalogApi.listings(resolved)).listings : []);
    } catch (error) {
      setBrowse([]);
      fail(error);
    }
  };

  const openListing = (listing: Listing) => {
    setBrowsing(listing);
    setStage("browseItem");
  };

  const openService = (offering: CatalogOffering) => {
    setBrowsingService(offering);
    setStage("browseService");
  };

  /** Adds an application to the plan, or takes it out. One added while browsing joins the curated list too. */
  const toggleAdd = (listing: Listing) => update((s) => {
    const id = listing.id;
    const d = { ...s.decided };
    if (d[id] === "added") {
      delete d[id];
      return { decided: d, queue: stage === "browseItem" ? s.queue.filter((e) => !(e.picked && e.listing.id === id)) : s.queue };
    }
    d[id] = "added";
    return { decided: d, queue: s.queue.some((e) => e.listing.id === id) ? s.queue
      : [...s.queue, { listing, reasons: [], fit: "Possible" as const, picked: true }] };
  });

  const goStart = () => { setNotice(null); setStage("welcome"); };

  const adding = params.get("add");
  useEffect(() => {
    if (!schema || !adding) return;
    setParams({}, { replace: true });
    const offering = schema.offerings.find((o) => o.id === adding);
    if (!offering) {
      setNotice("We couldn't find that service in our catalog right now. Here is everything we offer.");
      void openBrowse();
      return;
    }
    update((s) => ({ services: s.services.includes(offering.id) ? s.services : [...s.services, offering.id] }));
    openService(offering);
    void loadBrowse();
    // Runs once the schema is in, for the link the visitor arrived with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, adding]);

  if (!schema) {
    return (
      <div ref={topRef} className="relative flex min-h-[calc(100dvh-4rem)] flex-col pt-6 pb-6">
        <Seo title="Catalog | Contextral" description="Find the automations made for your business in about 3 minutes." />
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={crumbs} />
        </div>
        <section className="my-auto w-full">
          {notice ? (
            <div className="flex flex-col items-center gap-4 py-16 text-center">
              <p className="text-lg text-text-secondary">{notice}</p>
              <button type="button" onClick={loadSchema} className="font-semibold text-primary hover:underline">Try again</button>
            </div>
          ) : <Loader label="Getting the catalog ready" />}
        </section>
      </div>
    );
  }

  const slots = blueprintSlots(schema, answers, details, setup, done, followUps);
  const activeSlot = stage === "q" ? (stageOf(question) === 0 && question !== "problem" ? "shows" : question === "team" ? "business" : question) : "";
  const blueprint = (layout: "column" | "grid") => (
    <Blueprint slots={slots} layout={layout} active={activeSlot} onChange={jumpTo}
               canChange={(slot) => slot.filled || queue.length > 0} />
  );
  const added = queue.filter((e) => decided[e.listing.id] === "added");
  const allSkipped = queue.length > 0 && queue.every((e) => decided[e.listing.id] === "skipped");
  const problemWords = [...answers.problems, details.problemText].filter(Boolean).slice(0, 2);
  const workspaceLabel = schema.picks.find((p) => p.id === "workspace")?.options.find((o) => o.id === answers.picks.workspace)?.label;
  const hubLine = `We matched ${queue.length === 1 ? "one application" : `${queue.length} applications`}${
    problemWords.length ? ` to ${joinWords(problemWords.map((p) => p.toLowerCase()))}` : " to your answers"}, picked for ${
    answers.team_size ? `a team of ${answers.team_size.toLowerCase()}` : "your team"}${workspaceLabel ? ` on ${workspaceLabel}` : ""}. Add the ones you want to your plan.`;
  const lead = { hub: hubLine, book: "We'll go over your selections, timing and pricing with you.",
                 browse: "Ready-made applications and services our team sets up for you. Open any of them to see how it works." }[stage as string];
  const stageStep = stage === "q" ? stageOf(question) : ["building", "hub", "item"].includes(stage) ? 2 : null;

  const startOver = () => { reset(); setNotice(null); setStage("welcome"); };

  // Where the visitor is, as a trail they can click back along. Structured data only lists the real pages.
  const listCrumb: BreadcrumbItem = { name: "Your curated list", path: "/catalog", onClick: () => setStage("hub") };
  const planCrumb: BreadcrumbItem = { name: "Your plan", path: "/catalog", onClick: () => setStage("plan") };
  const trail: BreadcrumbItem[] = {
    welcome: [],
    q: [{ name: "Your assessment", path: "/catalog" }],
    building: [{ name: "Your assessment", path: "/catalog" }],
    details: [{ name: "Your assessment", path: "/catalog", onClick: () => jumpTo("problem") }, listCrumb],
    hub: [listCrumb],
    nofit: [listCrumb],
    item: [listCrumb, { name: queue[position]?.listing.name ?? "", path: "/catalog" }],
    plan: [...(queue.length ? [listCrumb] : []), planCrumb],
    book: [...(queue.length ? [listCrumb] : []), planCrumb, { name: "Book a call", path: "/catalog" }],
    browse: [{ name: "Browse everything", path: "/catalog" }],
    browseItem: [{ name: "Browse everything", path: "/catalog", onClick: () => setStage("browse") },
                 { name: browsing?.name ?? "", path: "/catalog" }],
    browseService: [{ name: "Browse everything", path: "/catalog", onClick: () => setStage("browse") },
                    { name: browsingService?.name ?? browsingService?.headline ?? "", path: "/catalog" }],
  }[stage];
  const breadcrumbs: BreadcrumbItem[] = trail.length ? [crumbs[0], { ...crumbs[1], onClick: goStart }, ...trail] : crumbs;

  const progress = saved ?? (session.done.length || session.queue.length ? session : null);
  const resumeLine = progress && stage === "welcome"
    ? `You told us: ${[progress.answers.problems.join(", ") || progress.details.problemText,
                       progress.answers.tools.length ? `${progress.answers.tools.length} tools` : ""].filter(Boolean).join(" · ") || "a few answers"}. ${
       progress.queue.length ? "Your curated list is waiting." : "Your curated list is almost ready."}`
    : null;

  const services = session.services.flatMap((id) => schema.offerings.filter((o) => o.id === id));
  const toggleService = (id: string) => update((s) => ({
    services: s.services.includes(id) ? s.services.filter((x) => x !== id) : [...s.services, id] }));
  const serviceSuggestions = (
    <ServiceSuggestions offered={session.offered} cards={schema.offerings} added={session.services} onToggle={toggleService} />
  );
  const answerFollowUp = (answer: string) => {
    const nextAnswers = { ...answers, details: [answers.details, answer].filter(Boolean).join("\n").slice(0, MAX_DETAILS) };
    update({ answers: nextAnswers });
    void build({ ...session, answers: nextAnswers }, (followUp?.asked ?? 0) + 1);
  };

  const cart: CartItem[] = [
    ...added.map((e) => (e.token ? { token: e.token } : { listing_id: e.listing.id })),
    ...services.map((o) => ({ offering_id: o.id })),
  ];
  const summary = [
    detailsNote(details),
    ...queue.filter((e) => decided[e.listing.id] === "skipped")
      .map((e) => `Not for them: ${e.listing.name}${session.notes[e.listing.id] ? ` (${session.notes[e.listing.id]})` : ""}`),
  ].filter(Boolean).join(". ");

  return (
    <div ref={topRef} className="relative flex min-h-[calc(100dvh-4rem)] flex-col pt-6 pb-6">
      <Seo title="Catalog | Contextral" description="Find the automations made for your business in about 3 minutes, then book a short call to go over your plan." />
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <Breadcrumbs items={breadcrumbs} schemaItems={crumbs} />
      </div>
      <section className="my-auto w-full">
        {stage !== "welcome" && (
          <div className="mb-8 flex flex-wrap items-end justify-between gap-x-6 gap-y-4 border-b border-borderSubtle pb-5">
            <div key={stage} className="catalog-enter flex min-w-0 flex-[1_1_420px] flex-col gap-1.5">
              {stage === "item" ? (
                <h1 className="mb-0 flex flex-wrap items-baseline gap-2 font-display text-2xl font-bold text-text-primary sm:text-3xl">
                  <button type="button" onClick={() => setStage("hub")} className="text-primary hover:underline">Your curated list</button>
                  <span aria-hidden="true" className="text-text-muted">›</span>
                  <span>{position + 1} of {queue.length}</span>
                </h1>
              ) : (
                <h1 className="mb-0 font-display text-2xl font-bold text-text-primary sm:text-3xl">{TITLES[stage]}</h1>
              )}
              {lead && <p className="text-text-secondary sm:text-lg">{lead}</p>}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {stageStep !== null && <StageBar current={stageStep} />}
              {stage.startsWith("browse") && (added.length > 0 || services.length > 0) && (
                <button type="button" onClick={() => setStage("plan")}
                        className="min-h-[44px] rounded-full border border-primary/40 bg-primary/10 px-4 text-sm font-bold text-primary">
                  See my plan ({added.length + services.length})
                </button>
              )}
              <button type="button" onClick={startOver} className="min-h-[44px] px-2 text-sm font-semibold text-text-muted hover:text-primary">
                Start over
              </button>
            </div>
          </div>
        )}
        {notice && <p role="alert" className="catalog-enter mx-auto mb-6 max-w-3xl rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{notice}</p>}

        <div key={stage} className="catalog-enter">
        {stage === "welcome" && (
          <Welcome resume={resumeLine} onStart={start} onBrowse={() => void openBrowse()}
                   onResume={() => {
                     if (!progress) return;
                     if (saved) restore(saved);
                     if (progress.queue.length) setStage("hub");
                     else { setEditing(true); ask(questionList(progress.answers, progress.details).find((q) => !progress.done.includes(q)) ?? "problem",
                                                 "Welcome back. Here is where you left off."); }
                   }}
                   onStartOver={reset} />
        )}

        {stage === "q" && (
          <div className="flex flex-wrap items-start gap-8">
            <div key={question} className={`min-w-0 flex-[999_1_440px] catalog-${direction}`}>
              <QuestionStep key={question} question={question} schema={schema} answers={answers} details={details}
                            acknowledgement={ack} runs={setup?.explanation ?? null} onChange={change}
                            onContinue={() => commit(question, answers, details)}
                            onSkip={() => commit(question, answers, details, "No problem, we can skip that.")}
                            onBack={previous} />
            </div>
            <aside aria-label="Your blueprint" className="min-w-0 flex-[1_1_280px] md:sticky md:top-24">{blueprint("column")}</aside>
          </div>
        )}

        {stage === "building" && (
          <BuildingQueue step={buildStep} rows={[
            { label: "Your problem", value: problemWords.join(", ") || "Not given" },
            { label: "Your tools", value: answers.tools.length ? joinWords(answers.tools.slice(0, 4)) : "We'll pick them with you" },
            { label: "Where it will run", value: setup?.explanation ?? "We'll decide this with you" },
            { label: "Your curated list", value: buildStep >= 4 ? "Applications matched to your problems" : "Matching against our catalog" },
          ]} />
        )}

        {stage === "hub" && (
          <QueueHub queue={queue} decided={decided} viewed={session.viewed} fanned={fanned} answers={blueprint("grid")}
                    extra={serviceSuggestions}
                    onStart={() => openItem(Math.max(0, queue.findIndex((e) => !decided[e.listing.id] && !session.viewed.includes(e.listing.id))))}
                    onPlan={() => setStage("plan")}
                    onRestart={() => { update({ decided: Object.fromEntries(added.map((e) => [e.listing.id, "added" as const])), viewed: [] }); openItem(0); }} />
        )}

        {stage === "item" && queue[position] && (
          <div key={position} className={`catalog-${direction}`}>
          <QueueItem entry={queue[position]} position={position} total={queue.length}
                     added={decided[queue[position].listing.id] === "added"} explanation={setup?.explanation ?? null}
                     answers={answers} details={details} tools={schema?.tools ?? []} offerings={schema.offerings}
                     onNext={next}
                     onToggleAdd={() => toggleAdd(queue[position].listing)}
                     onSkip={(reason) => {
                       const id = queue[position].listing.id;
                       update((s) => ({ decided: { ...s.decided, [id]: "skipped" }, notes: { ...s.notes, [id]: reason } }));
                       next();
                     }} />
          </div>
        )}

        {stage === "plan" && (
          <YourPlan items={added} services={services} runs={setup?.explanation ?? null} allSkipped={allSkipped}
                    onRemoveService={toggleService} extra={serviceSuggestions}
                    onBook={() => setStage("book")}
                    onRestart={() => { update({ decided: Object.fromEntries(added.map((e) => [e.listing.id, "added" as const])), viewed: [] }); openItem(0); }}
                    onRemove={(id) => update((s) => {
                      const d = { ...s.decided };
                      delete d[id];
                      return { decided: d, queue: s.queue.filter((e) => !(e.picked && e.listing.id === id)) };
                    })} />
        )}

        {stage === "book" && (
          <div className="mx-auto max-w-3xl">
            <CatalogCheckout picks={answers.picks} answers={answersForDashboard(answers, details)} cart={cart} transcript={[]}
                             summary={summary} onBack={() => setStage("plan")} onBooked={() => update({ decided: {}, notes: {}, viewed: [] })} />
          </div>
        )}

        {stage === "nofit" && (
          <NothingFits said={details.problemText} suggestions={suggestions} onChange={() => jumpTo("problem")}
                       onBrowse={() => void openBrowse()} extra={serviceSuggestions}
                       onPlan={added.length || services.length ? () => setStage("plan") : undefined} />
        )}

        {stage === "details" && followUp && (
          <DetailsStep key={followUp.asked} question={followUp.question} onSubmit={answerFollowUp}
                       onSkip={() => setStage("nofit")} />
        )}

        {stage === "browse" && (browse
          ? <BrowseAll listings={browse} offerings={schema.offerings} onStart={start} onOpen={openListing}
                       onOpenService={openService} added={added.map((e) => e.listing.id)} services={session.services} />
          : <Loader label="Loading everything in our catalog" compact />)}

        {stage === "browseItem" && browsing && (
          <QueueItem entry={queue.find((e) => e.listing.id === browsing.id)
                            ?? { listing: browsing, reasons: [], fit: "Possible", picked: true }}
                     position={0} total={1} added={decided[browsing.id] === "added"} explanation={setup?.explanation ?? null}
                     answers={answers} details={details} tools={schema.tools} offerings={schema.offerings}
                     onToggleAdd={() => toggleAdd(browsing)} onNext={() => setStage("browse")} nextLabel="Keep browsing" />
        )}

        {stage === "browseService" && browsingService && (
          <ServicePage offering={browsingService} added={session.services.includes(browsingService.id)}
                       alongside={(browse ?? []).filter((l) => l.needs?.includes(browsingService.id))}
                       onToggle={() => toggleService(browsingService.id)} onOpenListing={openListing}
                       onBack={() => setStage("browse")} />
        )}
        </div>
      </section>
    </div>
  );
};

export default Catalog;
